import { Component, Injectable, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { GlobalService } from 'app/global/global.service';
import { ContractControlService, SECTION_TYPE } from 'app/main/pages/contract-control/contract-control.service';
import { SharedStoreFacade } from 'app/main/shared/shared/store/shared-store.facade';
import { catchError, forkJoin, map, Observable, of, skipWhile, Subject, switchMap, take, takeUntil, tap } from 'rxjs';
import uuid from 'short-unique-id';
import { ContactsData } from '../../../../../global/integrations/integration.dto';
import { FilesValidatorsService } from '../../../../shared/validators/files-validators';
import { IAddRecipientRequestParams, RecipientsService } from '../../../contacts/recipients/recipients.service';
import { IGetContractControlDataResponse } from '../../../contract-control/store/dto/contract-control-view';
import { Recipient } from '../../../edit-template/edit-template.dto';
import { DocumentsService } from '../../documents.service';
import { DocumentUploadFileApiService } from '../../modals/document-file-upload-modal/document-upload-file.api.service';
import { DocumentListingFacade } from '../../store/document-listing.facade';
import { AiCcImportService, IAutoFillUploadFile } from './create-cc-by-ai-form/ai-cc-import.service';
import { CustomBoxManupilator } from './custom-box/custom-box-manupilator';
import { ConfirmCloseModalComponent } from 'app/main/pages/general-modals/confirm-close-modal/confirm-close-modal.component';
import { HttpErrorResponse } from '@angular/common/http';
import { DocumentToCCAndLinkerService } from 'app/main/pages/contract-control/contract-control-detail-view/cc-documents/document-contract-link.service';
import { DocumentTypeCustomBoxes, IDocumentTypesDto } from '../../dto/document-listing.dto';
import { DocumentTypeSectionEnum, IBoxTypeEnum } from 'app/main/pages/settings/documenttype/document-type-settings-modal/default-constants/document-type-settings.constants';
import { IContractControlData } from 'app/main/pages/contract-control/contract-control.dto';
import { ICostForm } from 'app/main/pages/contract-control/contract-control-detail-view/cc-cost/cost.dto';
import { IRevenueForm } from 'app/main/pages/contract-control/contract-control-detail-view/cc-revenue/revenue.dto';

export const DEFAULT_CONTRACT_CONTROL_NAME = '';
export const DEFAULT_CONTRACT_CONTROL_ID = '';
export const DEFAULT_CONTRACT_CARD_IS_ACTIVE = 0;


export const DEFAULT_CONTRACT_LENGTH = '12';
export const DEFAULT_REVENUE_CURRENCY = 'SEK';
export const DEFAULT_COST_CURRENCY = 'SEK';
export const DEFAULT_COST_PERIOD = 'm';

export const DEFAULT_REVENUE_PERIOD = 'm';
export const DEFAULT_PERIOD_RENEWAL_IS_ACTIVE = 0;
export const DEFAULT_PERIOD_RENEWAL_AUTO_RENEW = false;

export const DEFAULT_NOTICE_PERIOD = 'd';
export const DEFAULT_CONTRACT_FORM_CONTRACT_TYPE = 1;
export const DEFAULT_CONTRACT_FORM_CONTRACT_NO = '';

export interface IAddRecipientRequestParamsWithId extends IAddRecipientRequestParams {
  id?: number
}

export enum SectionEnum {
  CONTRACT_CONTROL = "contract_control",
  DOCUMENTS_SECTION = "documents_section"
}
@Component({
  selector: 'app-add-to-contract-control-modal',
  templateUrl: './add-to-contract-control-modal.component.html',
  styleUrl: './add-to-contract-control-modal.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class AddToContractControlModalComponent implements OnInit, OnDestroy {

  private readonly _destroyed$ = new Subject<void>();

  @Input() section: SectionEnum = SectionEnum.DOCUMENTS_SECTION;

  public documentId: number;
  public progress: number = 0;
  public currentPageIndex = 0;
  protected stepsCount = 1;
  public loading: boolean = false;
  public processingPDF: boolean = false;
  public uploadAIFormSubmitted: boolean = false;
  public uuid = new uuid({ length: 13 });
  public addTocontractForm: FormGroup;
  public stepFormKeys = [
    'contractForm',
    'periodRenewalForm',
    'costForm',
    'revenueForm',
    'managers',
    'contacts',
    'customBoxes',
  ];

  public initialStep = true;
  public documentTypes$ = this._sharedStoreFacade$.documentTypes$;
  public documentData$ = this._documentFacade$.selectedDocument$;
  public contractData$ = this._documentFacade$.contractData$;
  public boxesSettings = {};

  public _searchedContracts$: IContractControlData[] = [];
  public _searchContractKeyword: string = 'contract_control_name';
  public contractLoading: boolean = false;
  protected aiDataExtracted: IAutoFillUploadFile;
  public acceptableFiles = ["application/pdf"]
  public maxFileSize: number = 20 * 1024 * 1024;
  filesValidation: ValidatorFn[] = [
    this.filesValidatorsService.maxFileSizeValidator(this.maxFileSize),
    this.filesValidatorsService.fileTypeValidator(this.acceptableFiles),
    Validators.required
  ];

  /**
  * Contructor
  */
  constructor(
    private _activeModal: NgbActiveModal,
    private _modalService: NgbModal,
    private _formBuilder: FormBuilder,
    private _documentFacade$: DocumentListingFacade,
    private _documentService$: DocumentsService,
    private _contractControlService$: ContractControlService,
    private _globalService$: GlobalService,
    private _router$: Router,
    private _customBoxManupilator: CustomBoxManupilator,
    private _sharedStoreFacade$: SharedStoreFacade,
    private filesValidatorsService: FilesValidatorsService,
    private aiCcImportService: AiCcImportService,
    private _addToContractControlFormHelper: AddToContractControlFormHelper,
    private _documentUploadFileApiService: DocumentUploadFileApiService,
    private _documentToCCAndLinkerService: DocumentToCCAndLinkerService,
    private _addToContractBeforeSaveActions: AddToContractBeforeSaveActions,
    private _translocoService$: TranslocoService,
  ) { }


  ngOnInit(): void {
    this.initialStep = true;
    this.initForm();
    this.documentData$.pipe(skipWhile(result => !result), take(1)).subscribe((result) => {
      if (this.section === SectionEnum.DOCUMENTS_SECTION) {
        const contractForm = this.addTocontractForm.get('contractForm');
        const { template_details, template } = result;
        contractForm?.get('document_type_id').setValue(template_details?.document_type_id ?? null);
        contractForm?.get('category_ids').setValue(template_details?.category_id ? [template_details.category_id] : []);
        contractForm?.get('contract_control_name').setValue(template?.name ?? '');

        const uniqueId = template_details?.document_number || this.uuid();
        contractForm.get('contract_no').setValue(uniqueId);
        this.documentId = template?.id;
      }
    });
  }

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
  }


  initForm() {
    this.aiDataExtracted = null;
    this.addTocontractForm = this._formBuilder.group({
      connected_contracts: this._formBuilder.array([]),
      contractForm: this._addToContractControlFormHelper.getContractFormGroup(),
      periodRenewalForm: this._addToContractControlFormHelper.getPeriodRenewalForm(),
      costForm: this._formBuilder.array([]),
      revenueForm: this._formBuilder.array([]),
      managers: this._formBuilder.array([]),
      contacts: this._formBuilder.array([]),
      customBoxes: this._formBuilder.array([]),
      autoFillUploadFile: this.getAutoFillUploadFileForm()
    });

    this.addTocontractForm.get('contractForm').get('document_type_id').valueChanges
      .pipe(
        switchMap(documentTypeId =>
          this.documentTypes$.pipe(
            takeUntil(this._destroyed$),
            map(result => result.find(item => item.id === documentTypeId)),
            tap((documentType: IDocumentTypesDto) => {
              const section = documentType?.sections?.find(section => section.name === DocumentTypeSectionEnum.ContractControlLayout);
              const customBoxes = section?.boxes?.filter(box => box.type === IBoxTypeEnum.CUSTOM && box.status)
                .map(box => ({
                    ...box,
                    document_type_custom_boxes_data: box.fields
                })) as unknown as DocumentTypeCustomBoxes[];
              const customBoxesLength = this.getCustomBoxesLength(documentType);
              
              this.getCustomBoxesControl.clear();
              // Merge new properties from document_type_data
              const newSettings = {
                contractForm: 1,
                period_renewal: 1,
                cost: this.getBoxStatus(documentType, 'Cost'),
                revenue: this.getBoxStatus(documentType, 'Revenue'),
                manager: 1,
                contacts: 1,
                customBoxes: customBoxesLength,
                final_step: 1
              };
              this.boxesSettings = newSettings;
              this.updateProgress();
              
              if (customBoxesLength && customBoxes) {
                const manipulatedCustomBoxes = this._customBoxManupilator.manupilateCustomBoxes(customBoxes, this.aiDataExtracted?.meta_data);
                manipulatedCustomBoxes.forEach(box => {
                  const alreadyExists = this.getCustomBoxesControl.value.some(x => x.id === box.id);
                  if (alreadyExists === false) {
                    const formGroup = this._customBoxManupilator.createBoxGroup(box, this.aiDataExtracted?.meta_data);
                    this.getCustomBoxesControl.push(formGroup);
                  }
                });
              }
            })
          )
        )
      )
      .subscribe();



    this.addTocontractForm.get('periodRenewalForm').get('is_contract_active')?.valueChanges
      .subscribe(value => {
        this.addTocontractForm.get('contractForm')?.get('is_active')?.setValue(value);
      });
  }


  /**
   * Gets the auto fill upload file form.
   */
  getAutoFillUploadFileForm() {
    this.uploadAIFormSubmitted = false;
    return this._formBuilder.group({
      file: [null, this.filesValidation],
      document_type_id: [null, [Validators.required]],
    })
  }



  private getCustomBoxesLength(documentType: IDocumentTypesDto): number {
    const section = documentType.sections.find(section => section.name === DocumentTypeSectionEnum.ContractControlLayout)?.boxes;
    return section.filter(box => box.type === IBoxTypeEnum.CUSTOM && box.status).length;
  }


  private getBoxStatus(documentType: IDocumentTypesDto, boxName: string): boolean {
    const section = documentType.sections.find(section => section.name === DocumentTypeSectionEnum.ContractControlLayout)?.boxes;
    return section.some(box => box.name === boxName && box.status);
  }




  /**
   * Get Custom Box Control
   */
  get getCustomBoxesControl() {
    return this.addTocontractForm.controls['customBoxes'] as FormArray;
  }


  // Access and manipulate form control values within the customBoxesControl
  getCustomBoxName(index: number): string {
    return this.getCustomBoxesControl.at(index).value.name;
  }

  addConfirmCloseModalClick() {
    const modalRef = this._modalService.open(ConfirmCloseModalComponent,
      {
        size: 'sm',
        backdrop: 'static',
        keyboard: false,
      });
    modalRef.result.then(
      (result) => {
        if (result === 'confirmed') {
          this._activeModal.close();
        }
      },
      () => {
      }
    );
  }

  /**
   * Go to prev section
   */
  prevPage() {
    if (this.currentPageIndex === 0 && this.initialStep === false) {
      // we are on the first step and want to go back into initials step we have to clear existing data
      this.initialStep = true;
      this.initForm();
      return;
    }

    if (this.currentPageIndex > 0) {
      this.updateProgressAndPage(-1);
    }
  }



  /**
   * Update NGRX State
   * @param key 
   * @param data 
   */
  updateState(key: string, data: string | number) {
    if (key !== 'customBoxes') {
      this._documentFacade$.setDocumentData(key, data);
    }
  }


  get activeBoxes() {
    return Object.keys(this.boxesSettings).filter(key => this.boxesSettings[key] == 1);
  }



  /**
   * Update Progress and page
   * @param delta 
   */
  updateProgressAndPage(delta: number) {
    const keys = Object.keys(this.boxesSettings);
    this.updatePageNumber(delta, keys);
  }



  updatePageNumber(delta: number, keys: string[]) {
    this.stepsCount += delta;
    this.currentPageIndex += delta;
    while (this.stepsCount > 0 && this.stepsCount < keys.length && this.boxesSettings[keys[this.stepsCount - 1]] === 0) {
      this.stepsCount += delta;
    }
    this.updateProgress();
  }



  updateProgress() {
    this.progress = (100 / this.activeBoxes.length) * (this.currentPageIndex + 1);
  }




  /**
   * Change page
   * @param delta 
   * @returns 
   */
  changePage(delta: number): void {
    this._documentFacade$.updateCurrentFormSubmittion(true);
    if (this.addTocontractForm.get(this.stepFormKeys[this.currentPageIndex]).invalid) return;
    this.updateState(this.stepFormKeys[this.currentPageIndex], this.addTocontractForm.controls[this.stepFormKeys[this.currentPageIndex]]?.value);
    this.updateProgressAndPage(delta);
    this._documentFacade$.updateCurrentFormSubmittion(false);
  }




  saveAndConnectContract() {
    this.loading = true;

    const formData = this.addTocontractForm.value;

    delete formData.autoFillUploadFile;

    const costForm = formData?.costForm ?? [];
    const revenueForm = formData?.revenueForm ?? [];

    const updatedResult = {
      ...formData,
      costForm: this.normalizeCostForm(costForm),
      revenueForm: this.normalizeRevenueForm(revenueForm),
      customBoxes: this.addTocontractForm.value?.customBoxes?.map(item => ({
        ...item,
        document_type_custom_boxes_data: item.document_type_custom_boxes_data.map(box => ({
          ...box,
          order: box.field_order
        }))
      }))
    };

    const routeConfig = {
      'documents_section': { service: this._documentService$, method: 'linkContract' },
      'contract_control': { service: this._contractControlService$, method: 'createContract' }
    };

    if (routeConfig[this.section]) {
      const { service, method } = routeConfig[this.section];
      const payload = this.section === SectionEnum.CONTRACT_CONTROL ? updatedResult : { id: this.documentId, updatedResult: updatedResult };

      this._addToContractBeforeSaveActions
        .setupData(payload)
        .pipe(
          tap((modifedData: { contacts?: ContactsData[] }) => {
            this.updateContactListAfterNewItemCreation(modifedData?.contacts);
          }),
          switchMap(payload => service[method](payload))
        )
        .pipe(take(1))
        .subscribe({
          next: (response) => {
            this.performContractSuccessAction(response);
          },
          error: (error: HttpErrorResponse) => {
            this._globalService$.toastrError(error?.error?.message || error.message || this._translocoService$.translate('An error occurred while processing your request. Please try again.'));
            this.loading = false;
          }
        });
    }
  }
  updateContactListAfterNewItemCreation(contacts: ContactsData[]) {
    if (contacts && contacts.length > 0) {
      this.addTocontractForm.get('contacts').patchValue(contacts);
    }
  }

  private performContractSuccessAction(response) {
    this.loading = false;
    if (this.section === SectionEnum.DOCUMENTS_SECTION) {
      this.connectContract(response);
    } else {
      this.attachPdfFileToCC(response, this.addTocontractForm?.get('autoFillUploadFile')?.get('file'))
        .pipe(take(1))
        .subscribe(() => {
          this._activeModal.close();
          this._router$.navigateByUrl('contract-control/view/' + response.id);
        })
    }
  }



  private connectContract(response): void {
    const formData = { contract_control_id: response.id, sectionable_id: this.documentId, section_type: SECTION_TYPE.DOCUMENT, contract_control: response }
    this._documentFacade$.linkContract(formData);
  }




  searchContract($event: string = '') {
    if ($event === '') return false;
    this.contractLoading = true;
    this._contractControlService$.fetchContractsPost({ page: 1, q: $event }).pipe(
      map(response => {
        this._searchedContracts$ = (response)?.data;
        this.contractLoading = false;
      }),
      catchError(error => {
        this.contractLoading = false;
        return of(this._globalService$.toastrError(error))
      })
    ).subscribe();
  }




  clearContractSearch() {
    this._searchedContracts$ = [];
  }




  selectContract($item: IContractControlData) {
    const formData = { contract_control_id: $item.id, sectionable_id: this.documentId, section_type: 'document', contract_control: $item }
    this._documentFacade$.connectContract(formData);
    this._activeModal.close();
  }


  skipUpload() {
    this.initForm();
    this.aiDataExtracted = null;
    this.initialStep = false;
  }

  beginCCCreation() {
    this.aiDataExtracted = null;
    const form = this.addTocontractForm.get('autoFillUploadFile');
    this.uploadAIFormSubmitted = true;

    if (form.valid) {
      this.processingPDF = true;
      const file: File = form?.value?.file;
      const documentTypeId: number = form?.value?.document_type_id;
      this.aiCcImportService.autofillUploadFile(file, documentTypeId)
        .pipe(
          tap(() => this._sharedStoreFacade$.getDocumentTypes()),
          take(1)
        )
        .subscribe({
          next: (data) => {
            this.aiDataExtracted = data;
            this.initialStep = false;
            this.addTocontractForm.get('contractForm').get('document_type_id').setValue(documentTypeId);
          },
          error: () => {
            this.aiDataExtracted = null;
            this.processingPDF = false;
            this._globalService$.toastrError(this._translocoService$.translate('Uploaded file validation error (Auto-Fill Form via OpenAI PDF) -Something went wrong with the AI parsing of the document'));
          },
          complete: () => {
            this.processingPDF = false;
          }
        });
    }
  }

  private attachPdfFileToCC(newCreatedCC: IGetContractControlDataResponse, fileControl: AbstractControl): Observable<void> {
    return new Observable(observer => {
      if (!fileControl?.value || fileControl?.invalid) {
        //no file has been attached - no need to make any actions
        observer.next();
        observer.complete();
      } else {
        return this.uploadFile(fileControl.value, newCreatedCC.document_type_id, Array.isArray(newCreatedCC.category_ids) ? newCreatedCC.category_ids : (newCreatedCC.category_ids ? [newCreatedCC.category_ids] : []))
          .pipe(
            skipWhile(x => x.percent !== 100 && x.response != null),
            map(x => x.response),
            switchMap(uplodedFileResponse => this._documentToCCAndLinkerService.linkDocumentToCC(
              uplodedFileResponse as unknown as { name: string, id: number },
              newCreatedCC.id,
              fileControl.value))
          )
          .pipe(take(1)).subscribe({
            next: () => {
              observer.next();
              observer.complete();
            }
          })
      }
    });
  }



  private uploadFile(file: File, documentTypeId: number, categoryIds?: number | number[]) {
    return this._documentUploadFileApiService.uploadFile(file, {
      document_type_id: documentTypeId,
      category_id: categoryIds as any // Backend might still expect category_id but we pass array
    })
  }

  private normalizeCostForm(costs: ICostForm[] = []): ICostForm[] {
    const systemCurrency = this._globalService$.getSystemCurrency();

    return (costs || []).map(cost => ({
      ...cost,
      cost_currency: cost?.cost_currency || systemCurrency
    }));
  }

  private normalizeRevenueForm(revenues: IRevenueForm[] = []): IRevenueForm[] {
    const systemCurrency = this._globalService$.getSystemCurrency();

    return (revenues || []).map(revenue => ({
      ...revenue,
      revenue_currency: revenue?.revenue_currency || systemCurrency
    }));
  }
}

@Injectable({
  providedIn: 'root'
})
export class AddToContractBeforeSaveActions {
  constructor(private _recipientsService: RecipientsService) {
  }


  setupData(data: { contacts?: IAddRecipientRequestParamsWithId[] }): Observable<{ contacts?: Recipient[] }> {
    return new Observable((observer) => {
      if (data?.contacts?.length > 0) {

        const recipients = this.setupRecipients(data?.contacts);
        forkJoin(recipients)
          .pipe(take(1))
          .subscribe(x => {
            observer.next({ ...data, contacts: x.filter(y => y != null) });
            observer.complete();
          });

      } else {
        observer.next(data);
        observer.complete();
      }

    });
  }


  private setupRecipients(reciepients: IAddRecipientRequestParamsWithId[]): Observable<Recipient>[] {
    return reciepients.map(x => {
      if (this.shouldCreateRecipient(x)) {
        const newReciepient = {
          firstname: x.firstname,
          lastname: x.lastname,
          email: x.email,
          phone_no: x.phone_no,
          persnol_no: x.persnol_no,
          address: x.address,
          zipcode: x.zipcode,
          city: x.city
        } as IAddRecipientRequestParams
        return this._recipientsService.addRecipient(newReciepient as unknown as IAddRecipientRequestParams)
          .pipe(
            map(x => ({
              ...x,
              sectionable_id: x.id
            })),
            catchError(() => of(null))
          );
      } else {
        return of(x as Recipient);
      }
    })

  }

  private shouldCreateRecipient(recipient: IAddRecipientRequestParamsWithId) {
    return recipient.id === null;
  }




}

@Injectable({
  providedIn: 'root'
})
export class AddToContractControlFormHelper {
  constructor(
    private _formBuilder: FormBuilder,
    private _globalService: GlobalService
  ) {
  }

  getContractFormGroup() {
    return this._formBuilder.group({
      id: [DEFAULT_CONTRACT_CONTROL_ID],
      contract_control_name: [DEFAULT_CONTRACT_CONTROL_NAME, [Validators.required]], //, [Validators.required]
      document_type_id: [null, [Validators.required]], //, [Validators.required]
      category_ids: [null],
      organization_id: [null],
      contract_type: [DEFAULT_CONTRACT_FORM_CONTRACT_TYPE],
      contract_no: [DEFAULT_CONTRACT_FORM_CONTRACT_NO],
      is_active: [DEFAULT_CONTRACT_CARD_IS_ACTIVE],
    })
  }


  getPeriodRenewalForm() {
    return this._formBuilder.group({
      id: [null],
      start_date: [null], //, [Validators.required]
      end_date: [null],
      contract_length: [DEFAULT_CONTRACT_LENGTH],
      notice_date: [null],
      notice: [null, [Validators.min(0)]],
      notice_period: [DEFAULT_NOTICE_PERIOD],
      auto_renew: [DEFAULT_PERIOD_RENEWAL_AUTO_RENEW, [Validators.min(0)]],
      contract_length_renew: [DEFAULT_CONTRACT_LENGTH],
      auto_renewal_date: [null],
      notice_date_renew: [null],
      notice_renew: [null],
      notice_period_renew: [null],
      other_length_renew: [null],
      other_length_period_renew: [null],
      is_contract_active: [DEFAULT_PERIOD_RENEWAL_IS_ACTIVE]
    });
  }


  getCostForm() {
    return this._formBuilder.group({
      cost_amount: [null, [Validators.min(0)]],
      cost_startup: [null, Validators.min(0)],
      cost_duration: [null, Validators.min(0)],
      cost_period: [DEFAULT_COST_PERIOD],
      cost_currency: [this._globalService.getSystemCurrency() || DEFAULT_COST_CURRENCY],
    });
  }


  getRevenueForm() {
    return this._formBuilder.group({
      revenue_amount: [null, Validators.min(0)],
      revenue_currency: [this._globalService.getSystemCurrency() || DEFAULT_REVENUE_CURRENCY],
      revenue_startup: [null, [Validators.min(0)]],
      revenue_duration: [null, Validators.min(0)],
      revenue_period: [DEFAULT_REVENUE_PERIOD],
    });
  }

  getAIValueIfNotModifedByUser(control: AbstractControl, AIValue: string | number | Date | boolean): string | number | Date | boolean {

    if (this.validateAIData(control, AIValue)) {
      return control?.dirty ? control.value : AIValue;
    }
    return control.value;

  }

  private validateAIData(formControl: AbstractControl, value: string | number | boolean | Date) {
    const tempControl = new FormControl(value, formControl?.validator);
    return tempControl.valid;
  }

}
